import React, { useState } from 'react';
import SearchableSelect from './SearchableSelect';

const Dashboard = ({ rows: initialRows, totalCampaigns: initialTotal, geos, filters: initialFilters }) => {
  const [rows, setRows] = useState(initialRows || []);
  const [totalCampaigns, setTotalCampaigns] = useState(initialTotal || 0);
  const [filters, setFilters] = useState(initialFilters || { from: '', to: '', geo: 'all' });
  const [loading, setLoading] = useState(false);

  const fetchData = async (next) => {
    setLoading(true);
    const params = new URLSearchParams();
    if (next.from) params.set('from', next.from);
    if (next.to) params.set('to', next.to);
    if (next.geo && next.geo !== 'all') params.set('geo', next.geo);
    try {
      const res = await fetch(`/dashboard.json?${params.toString()}`, {
        headers: { 'X-Requested-With': 'XMLHttpRequest' },
      });
      const data = await res.json();
      setRows(data.rows || []);
      setTotalCampaigns(data.totalCampaigns || 0);
    } catch (e) {
      console.error('Error loading dashboard:', e);
    }
    setLoading(false);
  };

  const update = (key, value) => {
    const next = { ...filters, [key]: value };
    setFilters(next);
    fetchData(next);
  };

  const num = (n) => Number(n || 0).toLocaleString();

  const totals = rows.reduce(
    (acc, r) => ({
      sent: acc.sent + r.sent,
      unique_players: acc.unique_players + r.unique_players,
      clicks: acc.clicks + r.clicks,
      deposit_sum: acc.deposit_sum + Number(r.deposit_sum || 0),
    }),
    { sent: 0, unique_players: 0, clicks: 0, deposit_sum: 0 }
  );

  const input =
    'shadow-sm border border-slate-300 rounded px-2 py-1.5 text-sm text-slate-700 focus:outline-none focus:ring-1 focus:ring-blue-500';

  return (
    <div className='min-h-screen bg-slate-50'>
      <div className='max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 py-6'>
        <div className='mb-6'>
          <h1 className='text-xl font-semibold text-slate-900'>Dashboard</h1>
        </div>

        <div className='bg-white rounded-lg border border-slate-200 p-3 mb-4 flex flex-wrap items-end gap-3'>
          <div className='min-w-[150px]'>
            <label className='block text-xs font-medium text-slate-500 mb-1'>From</label>
            <input type='date' className={`${input} w-full`} value={filters.from || ''} onChange={(e) => update('from', e.target.value)} />
          </div>
          <div className='min-w-[150px]'>
            <label className='block text-xs font-medium text-slate-500 mb-1'>To</label>
            <input type='date' className={`${input} w-full`} value={filters.to || ''} onChange={(e) => update('to', e.target.value)} />
          </div>
          <div className='min-w-[160px]'>
            <label className='block text-xs font-medium text-slate-500 mb-1'>GEO</label>
            <SearchableSelect
              options={geos}
              value={filters.geo || 'all'}
              onChange={(v) => update('geo', v || 'all')}
              includeAllOption
              placeholder='All GEO'
            />
          </div>
          {loading && <span className='text-sm text-slate-400'>Loading…</span>}
        </div>

        <div className='bg-white rounded-lg border border-slate-200 overflow-x-auto'>
          <table className='w-full text-sm'>
            <thead className='bg-slate-50 text-slate-500'>
              <tr>
                <th className='text-left font-medium px-4 py-2'>GEO</th>
                <th className='text-right font-medium px-4 py-2'>Campaigns</th>
                <th className='text-right font-medium px-4 py-2'>Sent</th>
                <th className='text-right font-medium px-4 py-2'>Unique players</th>
                <th className='text-right font-medium px-4 py-2' title='Total Rebrandly link transitions; unique recipients are unavailable'>Link transitions</th>
                <th className='text-right font-medium px-4 py-2'>Deposit sum ($)</th>
              </tr>
            </thead>
            <tbody className='divide-y divide-slate-100'>
              {rows.map((r) => (
                <tr key={r.geo} className='hover:bg-slate-50'>
                  <td className='px-4 py-2'><span className='text-xs px-2 py-0.5 rounded bg-blue-50 text-blue-700'>{r.geo}</span></td>
                  <td className='px-4 py-2 text-right text-slate-700'>{num(r.campaigns)}</td>
                  <td className='px-4 py-2 text-right text-slate-700'>{num(r.sent)}</td>
                  <td className='px-4 py-2 text-right text-slate-700'>{num(r.unique_players)}</td>
                  <td className='px-4 py-2 text-right text-slate-700'>{num(r.clicks)}</td>
                  <td className='px-4 py-2 text-right font-medium text-slate-900'>${num(r.deposit_sum)}</td>
                </tr>
              ))}
              {rows.length === 0 && (
                <tr><td colSpan='6' className='px-4 py-10 text-center text-slate-500'>No data for the selected period.</td></tr>
              )}
            </tbody>
            {rows.length > 0 && (
              <tfoot className='bg-slate-50 font-semibold text-slate-900'>
                <tr>
                  <td className='px-4 py-2'>Total</td>
                  <td className='px-4 py-2 text-right'>{num(totalCampaigns)}</td>
                  <td className='px-4 py-2 text-right'>{num(totals.sent)}</td>
                  <td className='px-4 py-2 text-right'>{num(totals.unique_players)}</td>
                  <td className='px-4 py-2 text-right'>{num(totals.clicks)}</td>
                  <td className='px-4 py-2 text-right'>${num(totals.deposit_sum.toFixed ? totals.deposit_sum.toFixed(2) : totals.deposit_sum)}</td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
